import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Editor } from '@tinymce/tinymce-react';
import { Type, Image as ImageIcon, User, Tag, Folder, X } from 'lucide-react';

const CreatePost = ({ token, onClose, onSuccess }) => {
  const [title, setTitle] = useState('');
  const [metaTitle, setMetaTitle] = useState(''); // <-- ADDED
  const [shortDesc, setShortDesc] = useState('');
  const [content, setContent] = useState('');
  const [image, setImage] = useState(null);

  const [authorName, setAuthorName] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const editorRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const seoCleanedContent = content.replace(/&nbsp;/g, ' ');

    const formData = new FormData();
    formData.append('title', title);
    formData.append('meta_title', metaTitle); // <-- ADDED
    formData.append('meta_description', shortDesc);
    formData.append('content', seoCleanedContent);
    formData.append('image', image);
    formData.append('author_name', authorName);
    formData.append('category', category);
    formData.append('tags', tags);

    try {
      await axios.post(`${process.env.REACT_APP_BASE_URL}/backend/api/posts`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      alert('Success! Article submitted for Admin review.');
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      const serverError = error.response?.data?.message || error.message;
      alert(`Error submitting post: ${serverError}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ADDED: Custom Image Upload Handler for TinyMCE
  const handleImageUpload = (blobInfo, progress) => {
    return new Promise(async (resolve, reject) => {
      const formData = new FormData();
      formData.append('image', blobInfo.blob(), blobInfo.filename());

      try {
        const res = await axios.post(`${process.env.REACT_APP_BASE_URL}/backend/api/upload-image`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          },
          onUploadProgress: (progressEvent) => {
            progress((progressEvent.loaded / progressEvent.total) * 100);
          }
        });
        // TinyMCE expects the URL of the uploaded image to be resolved
        resolve(res.data.url);
      } catch (error) {
        console.error('Upload failed:', error);
        reject('Image upload failed. Ensure the file is under 5MB.');
      }
    });
  };

  return (
    <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden w-full max-w-4xl max-h-[90vh] flex flex-col relative">

      <div className="bg-brand-dark p-6 border-b border-gray-800 flex justify-between items-center sticky top-0 z-10">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Type size={20} className="text-brand-cyan" /> Draft New Article
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors bg-white/10 hover:bg-white/20 p-2 rounded-full"
        >
          <X size={20} />
        </button>
      </div>

      <div className="overflow-y-auto p-8">
        <form onSubmit={handleSubmit} className="space-y-8">

          <div>
            <div className="flex justify-between items-end mb-2">
              <label className="block text-sm font-bold text-gray-700">Post Title</label>
              <span className="text-xs text-gray-400">{title.length}/60</span>
            </div>
            <input
              type="text" required maxLength={60} value={title}
              className="w-full p-4 rounded-xl border border-gray-200 focus:outline-none focus:border-brand-cyan focus:ring-1 transition-colors"
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div>
            <div className="flex justify-between items-end mb-2">
              <label className="block text-sm font-bold text-gray-700">SEO Meta Title (Used for URL Slug)</label>
              <span className="text-xs text-gray-400">{metaTitle.length}/60</span>
            </div>
            <input
              type="text" required maxLength={60} value={metaTitle}
              className="w-full p-4 rounded-xl border border-gray-200 focus:outline-none focus:border-brand-cyan focus:ring-1 transition-colors"
              onChange={(e) => setMetaTitle(e.target.value)}
            />
          </div>

          <div>
            <div className="flex justify-between items-end mb-2">
              <label className="block text-sm font-bold text-gray-700">Short Description</label>
              <span className="text-xs text-gray-400">{shortDesc.length}/160</span>
            </div>
            <textarea
              required maxLength={160} rows={3} value={shortDesc}
              className="w-full p-4 rounded-xl border border-gray-200 focus:outline-none focus:border-brand-cyan focus:ring-1 transition-colors resize-none"
              onChange={(e) => setShortDesc(e.target.value)}
            ></textarea>
          </div>

          {/* TINYMCE EDITOR */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Content</label>
            <Editor
              tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/7.1.1/tinymce.min.js"
              licenseKey="gpl"
              apiKey="b3o6jlpiksf6zr12043m53vq6xxp80wrmwo8tpa8c867f4sa"
              onInit={(_, editor) => (editorRef.current = editor)}
              value={content}
              onEditorChange={(newContent) => setContent(newContent)}
              init={{
                height: 450,
                menubar: true,
                plugins: [
                  "advlist", "autolink", "lists", "link", "image", "charmap",
                  "preview", "anchor", "searchreplace", "visualblocks", "code",
                  "fullscreen", "insertdatetime", "media", "table", "help", "wordcount",
                ],
                toolbar:
                  "undo redo | blocks | bold italic underline forecolor backcolor | alignleft aligncenter alignright | bullist numlist | link image media table | removeformat | help | code fullscreen",

                // ADDED: These lines tell TinyMCE to allow file selection and use our custom uploader
                image_title: true,
                automatic_uploads: true,
                file_picker_types: 'image',
                images_upload_handler: handleImageUpload
              }}
            />
            <p style={{ fontSize: "12px", color: "gray", marginTop: "6px" }}>
              💡 Tip: Press <strong>Enter twice</strong> between paragraphs to leave a blank line. You can now drag and drop images directly into the editor!
            </p>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Featured Image</label>
            <div className="border border-gray-200 rounded-xl p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors">
              <ImageIcon className="text-gray-400" size={24} />
              <input
                id="file-upload" type="file" required accept="image/*"
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-bold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 cursor-pointer transition-colors"
                onChange={(e) => setImage(e.target.files[0])}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                <User size={16} className="text-brand-cyan" /> Author Name
              </label>
              <input
                type="text" required value={authorName}
                className="w-full p-4 rounded-xl border border-gray-200 focus:outline-none focus:border-brand-cyan focus:ring-1 transition-colors"
                onChange={(e) => setAuthorName(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                <Folder size={16} className="text-brand-cyan" /> Category
              </label>
              <select
                required value={category}
                className="w-full p-4 rounded-xl border border-gray-200 focus:outline-none focus:border-brand-cyan focus:ring-1 transition-colors bg-white appearance-none"
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="" disabled>Select category</option>
                <option value="Commercial Signage">Commercial Signage</option>
                <option value="Vehicle Wraps">Vehicle Wraps</option>
                <option value="Promotional Products">Promotional Products</option>
                <option value="Industry News">Industry News</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
              <Tag size={16} className="text-brand-cyan" /> Tags (comma separated)
            </label>
            <input
              type="text" required value={tags} placeholder="e.g. Signs, Branding, Upland"
              className="w-full p-4 rounded-xl border border-gray-200 focus:outline-none focus:border-brand-cyan focus:ring-1 transition-colors"
              onChange={(e) => setTags(e.target.value)}
            />
          </div>

          <div className="pt-4 border-t border-gray-100">
            <button
              type="submit" disabled={isSubmitting}
              className="w-full py-4 bg-brand-blue text-white font-bold rounded-xl hover:bg-brand-dark transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed shadow-md"
            >
              {isSubmitting ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>Submit for Review</>
              )}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default CreatePost;